/**
 * MarketingV2CustomerRoute — the PUBLIC customer-safe landing experience.
 *
 * CRITICAL DESIGN RULES:
 *  - This component MUST NOT read localStorage/sessionStorage for role/token.
 *  - It MUST NOT change its output based on the caller's authentication state.
 *  - It renders identically for logged-out customers, Owner, and Receptionists.
 *  - No Owner banner. No Owner Test Mode. No diagnostic controls.
 *
 * The component receives the customer-safe projection from
 * `/api/marketing-v2/public/campaigns/:id` — a purpose-built endpoint that
 * only returns customer-safe fields.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { trackTextCtaExit, trackCallCtaExit } from "../../lib/analytics";
import PublicPrivacyLinks from "../../components/privacy/PublicPrivacyLinks";
import "./marketing_v2.css";

// P1.4B.2 — canonical anonymous journey ID (ua_anon_id). MV2 keeps its
// own pre-existing `mv2_visitor_id` for scope-local dedupe (unchanged),
// but ALSO sends the canonical anonymous_id so the server can bind the
// deterministic identity-resolution to Cascade's single canonical journey.
function _canonicalAnonId() {
  try {
    let id = localStorage.getItem("ua_anon_id");
    if (!id) {
      id = "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("ua_anon_id", id);
    }
    return id;
  } catch { return null; }
}

const API = process.env.REACT_APP_BACKEND_URL;

function getOrCreateVisitorId() {
  try {
    let v = localStorage.getItem("mv2_visitor_id");
    if (!v) {
      v = "vst_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("mv2_visitor_id", v);
    }
    return v;
  } catch {
    return "vst_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function getSessionId() {
  try {
    let s = sessionStorage.getItem("mv2_session_id");
    if (!s) {
      s = "ses_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("mv2_session_id", s);
    }
    return s;
  } catch {
    return "ses_" + Date.now().toString(36);
  }
}

function detectDeviceFamily() {
  if (typeof window === "undefined") return "desktop";
  return window.innerWidth < 900 ? "mobile" : "desktop";
}

export default function MarketingV2CustomerRoute({ isPreview = false }) {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claim, setClaim] = useState(null);
  const [error, setError] = useState(null);
  const [formState, setFormState] = useState({
    name: "", email: "", phone: "",
    promotional_email: false, promotional_text: false,
    delivery_choice: "on_screen",
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const visitorId = useMemo(getOrCreateVisitorId, []);
  const sessionId = useMemo(getSessionId, []);
  const [device, setDevice] = useState(detectDeviceFamily);

  const utm = {
    utm_source: searchParams.get("utm_source"),
    utm_medium: searchParams.get("utm_medium"),
    utm_campaign: searchParams.get("utm_campaign"),
    utm_content: searchParams.get("utm_content"),
    gclid: searchParams.get("gclid"),
    gbraid: searchParams.get("gbraid"),
    wbraid: searchParams.get("wbraid"),
    fbclid: searchParams.get("fbclid"),
    tracking_asset_code: searchParams.get("tc"),
  };

  useEffect(() => {
    const onResize = () => setDevice(detectDeviceFamily());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Checkpoint 7B — enforce noindex when policy dictates.
  // Also always add a `noindex` meta on /v2/c/* while the campaign is a
  // fixture preview or focused_campaign so search engines never absorb
  // temporary campaign copy. We inject/remove the meta tag imperatively.
  useEffect(() => {
    if (!projection) return;
    const wantsNoindex = projection.indexing_policy === "noindex"
      || projection.indexing_policy === "noindex_nofollow"
      || projection.route_purpose === "focused_campaign"
      || projection.route_purpose === "focused_completion_flow"
      || projection.campaign_temporary === true;
    let tag = document.querySelector('meta[name="robots"][data-mv2-owned="true"]');
    if (wantsNoindex) {
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "robots");
        tag.setAttribute("data-mv2-owned", "true");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", projection.indexing_policy === "noindex_nofollow"
        ? "noindex, nofollow"
        : "noindex");
    } else if (tag) {
      tag.parentNode.removeChild(tag);
    }
    return () => {
      const cleanup = document.querySelector('meta[name="robots"][data-mv2-owned="true"]');
      if (cleanup) cleanup.parentNode.removeChild(cleanup);
    };
  }, [projection]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/marketing-v2/public/campaigns/${campaignId}`);
        if (!res.ok) throw new Error("fetch_failed");
        const p = await res.json();
        if (!cancelled) {
          setProjection(p);
          setLoading(false);
        }
        // Fire landing_view (does not depend on role)
        fetch(`${API}/api/marketing-v2/public/events/landing-view`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            campaign_id: campaignId,
            visitor_id: visitorId,
            session_id: sessionId,
            device_family: device,
            ...utm,
          }),
        }).catch(() => {});
      } catch (e) {
        if (!cancelled) {
          setError("Something went wrong loading this offer.");
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [campaignId]);

  async function submitClaim(e) {
    e.preventDefault();
    setFormError(null);
    if (!formState.name.trim() || !formState.phone.trim()) {
      setFormError("Please add your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/marketing-v2/public/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign_id: campaignId,
          visitor_id: visitorId,
          session_id: sessionId,
          anonymous_id: _canonicalAnonId(),
          name: formState.name,
          email: formState.email || null,
          phone: formState.phone,
          consent: {
            promotional_email: formState.promotional_email,
            promotional_text: formState.promotional_text,
          },
          delivery_choice: formState.delivery_choice,
          device_family: device,
          ...utm,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setFormError(j?.detail?.code === "campaign_not_active"
          ? "This offer isn't active right now."
          : "We couldn't save that. Please try again.");
        setSubmitting(false);
        return;
      }
      const j = await res.json();
      setClaim(j.deal_claim);
    } catch {
      setFormError("Network problem. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onMethodClick(method) {
    fetch(`${API}/api/marketing-v2/public/events/method-click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        campaign_id: campaignId,
        visitor_id: visitorId,
        deal_claim_id: claim?.id || null,
        method,
        device_family: device,
      }),
    }).catch(() => {});
  }

  // P1.4B.1 — Text CTA handler.
  // 1) Fire the durable Text CTA-Exit funnel event and request an opaque
  //    reference token, both server-side (R-B1-08/12).
  // 2) Append the recognized "Ref {TOKEN}" line to the existing prefilled
  //    SMS body — copy is otherwise preserved exactly (R-B1-14).
  // 3) Never block native SMS on capture failure (R-B1-19).
  async function onTextCtaTap(e, surface) {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    onMethodClick("text");
    const phone = (salon.phone_e164 || "").trim();
    if (!phone) return;
    const baseBody = `Hi ${salon.name || "there"}, I'd like to check availability.`;
    let smsHref = `sms:${phone}?&body=${encodeURIComponent(baseBody)}`;
    try {
      const { ref_token } = await trackTextCtaExit(surface, {
        campaign_id: campaignId, landing_variant_id: null, offer_context: projection?.name_public,
      });
      const finalBody = ref_token ? `${baseBody}\nRef ${ref_token}` : baseBody;
      smsHref = `sms:${phone}?&body=${encodeURIComponent(finalBody)}`;
    } catch { /* fall through — native SMS must still open */ }
    try { window.location.href = smsHref; } catch { /* noop */ }
  }

  function onCallCtaTap(surface) {
    // Non-blocking — trackCallCtaExit uses keepalive; native tel: navigation
    // continues immediately per R-B1-38/39.
    try { trackCallCtaExit(surface, { campaign_id: campaignId, offer_context: projection?.name_public }); } catch { /* noop */ }
    onMethodClick("call");
  }

  // Create opaque handoff token, then navigate to /book?mv2_ho=<token>.
  // NEVER put deal_claim_id, name, email, or phone in the URL.
  async function goToBookingViaHandoff(method) {
    onMethodClick(method);
    if (!claim) return;
    try {
      const res = await fetch(`${API}/api/marketing-v2/public/handoff/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deal_claim_id: claim.id,
          visitor_id: visitorId,
          method,
        }),
      });
      if (!res.ok) throw new Error("handoff_failed");
      const j = await res.json();
      const asRequest = method === "appointment_request" ? "&as_request=1" : "";
      window.location.href = `/book?mv2_ho=${encodeURIComponent(j.token)}${asRequest}`;
    } catch {
      // Safe fallback: go to plain booking without prefill; the customer
      // will still see the truthful call-only or online CTA the resolver
      // reports.
      window.location.href = "/book";
    }
  }

  if (loading) return <div className="mv2-page"><div className="mv2-wrap"><div className="mv2-card">Loading…</div></div></div>;
  if (error) return <div className="mv2-page"><div className="mv2-wrap"><div className="mv2-unavailable">{error}</div></div></div>;
  if (!projection) return null;

  // Truthful unavailable states
  if (projection.customer_safe_state && projection.customer_safe_state !== "active") {
    const wrapper = (
      <div className="mv2-page" data-testid="mv2-public-page">
        <div className="mv2-wrap">
          <div className="mv2-unavailable" data-testid="mv2-unavailable-state" data-state={projection.customer_safe_state}>
            <h2>Offer unavailable</h2>
            <p>{projection.customer_safe_reason || "This link is not available right now."}</p>
          </div>
        </div>
      </div>
    );
    return isPreview ? previewFrame(wrapper, projection) : wrapper;
  }

  const resolution = projection.conversion_resolution || {};
  const salon = projection.business_info || {};
  const offer = projection.offer_public || {};
  const reg = projection.registration_treatment || {};
  const ds = projection.design_system || null;

  // When a Design System overlay is present, prefer its layout/execution.
  // Otherwise fall back to the legacy template-driven composition.
  const composition = ds
    ? (device === "mobile"
        ? (ds.layout_blueprint?.module_order || [])
        : (ds.layout_blueprint?.module_order || []))
    : (device === "mobile"
        ? projection.mobile_composition
        : projection.desktop_composition);

  const desktopMode = device !== "mobile";
  const layoutKind = ds?.layout_blueprint?.id || "legacy";
  const isSplitLayout = layoutKind === "lb_proof_and_claim_split_v1";
  // Checkpoint 7B — Business Introduction layouts.
  const isBiBrandNarrative = layoutKind === "lb_brand_and_trust_narrative_v1";
  const isBiReputationSplit = layoutKind === "lb_reputation_and_experience_split_v1";
  // Layout Laboratory — Business Introduction extensions.
  const isBiConversionFirst = layoutKind === "lb_conversion_first_compact_v1";
  const isBiVisualHero = layoutKind === "lb_brand_and_visual_hero_v1";
  const isBiServiceExplorer = layoutKind === "lb_service_explorer_first_v1";
  const isBusinessIntroduction = isBiBrandNarrative || isBiReputationSplit
    || isBiConversionFirst || isBiVisualHero || isBiServiceExplorer;

  // Content
  const heroBlock = (
    <div className="mv2-hero" data-testid="mv2-hero" data-relationship-stage={projection.relationship_stage}>
      {offer.urgency_text && <div className="mv2-urgency" data-testid="mv2-urgency">{offer.urgency_text}</div>}
      <h1 data-testid="mv2-hero-headline">
        {ds?.content_bundle?.headline || offer.display_copy || projection.name_public}
      </h1>
      {ds?.content_bundle?.supporting_copy && (
        <p data-testid="mv2-hero-supporting">{ds.content_bundle.supporting_copy}</p>
      )}
      {!ds && projection.relationship_stage === "cold" && (
        <p data-testid="mv2-hero-intro">Welcome to {salon.name}. {offer.customer_outcome}</p>
      )}
      {!ds && projection.relationship_stage === "existing" && (
        <p data-testid="mv2-hero-recognition">Welcome back. {offer.customer_outcome}</p>
      )}
    </div>
  );

  // Service outcome (DS strategic role) — headline-forward, no offer copy yet.
  const serviceOutcomeBlock = (
    <div className="mv2-card mv2-service-outcome" data-testid="mv2-service-outcome">
      <h1 style={{ marginTop: 0 }}>{ds?.content_bundle?.headline || offer.display_copy || projection.name_public}</h1>
      {ds?.content_bundle?.supporting_copy && <p>{ds.content_bundle.supporting_copy}</p>}
    </div>
  );

  // Aggregate rating block (visible only when the design-system aggregate
  // snapshot is present AND approved for public display).
  const aggregateBlock = ds?.aggregate && ds.aggregate.public_display_approved && (
    <div className="mv2-card mv2-aggregate" data-testid="mv2-aggregate">
      <div style={{ fontSize: 32, fontWeight: 700 }}>
        {"★".repeat(Math.round(ds.aggregate.rating || 0))}
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{Number(ds.aggregate.rating).toFixed(1)} / 5</div>
        <div style={{ color: "#6b7280", fontSize: 13 }}>
          Based on {ds.aggregate.review_count} guest reviews
        </div>
      </div>
    </div>
  );

  const featuredTestimonialBlock = ds && (ds.proof_reviews || []).length > 0 && (
    <div className="mv2-card mv2-featured-testimonial" data-testid="mv2-featured-testimonial">
      <div style={{ fontSize: 18, lineHeight: 1.5 }}>&ldquo;{ds.proof_reviews[0].text}&rdquo;</div>
      <div style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}>
        — {ds.proof_reviews[0].author_name} · {"★".repeat(ds.proof_reviews[0].rating || 5)}
      </div>
    </div>
  );

  const heroImageProofBlock = ds && (
    <div className="mv2-card mv2-hero-image-proof" data-testid="mv2-hero-image-proof">
      <div className="mv2-hero-image-placeholder"
           style={{
             background: "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
             color: "#fff", padding: "48px 20px",
             borderRadius: 12, textAlign: "center",
           }}>
        <div style={{ fontSize: 14, opacity: 0.85 }}>Featured guest result</div>
        <div style={{ fontSize: 24, fontWeight: 700, marginTop: 8 }}>
          {ds.content_bundle?.service_description?.slice(0, 60) || "Fresh, salon-perfect pedicure"}
        </div>
      </div>
      {featuredTestimonialBlock}
    </div>
  );

  const supportingReviewSetBlock = ds && (ds.proof_reviews || []).length > 1 && (
    <div className="mv2-card mv2-supporting-review-set" data-testid="mv2-supporting-review-set">
      <h2>More guest reviews</h2>
      {ds.proof_reviews.slice(1).map((p) => (
        <div className="mv2-proof-item" key={p.id}>
          <span className="mv2-rating">{"★".repeat(p.rating || 5)}</span>
          {p.text && <p>&quot;{p.text}&quot;</p>}
          <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>— {p.author_name}</p>
        </div>
      ))}
    </div>
  );

  const locationHoursBlock = (() => {
    const hasAddress = Boolean(salon.address);
    const hasHours = Boolean(salon.hours_summary);
    const hasPhone = Boolean(salon.phone_e164);
    // Suppress the module entirely if we have nothing at all.
    if (!hasAddress && !hasHours && !hasPhone) return null;
    // Checkpoint 10 · Section 15: never claim "when" without time information.
    //   • Hours present  → "Location & hours"
    //   • Hours missing  → "Location & contact" (truthful fallback)
    const heading = hasHours ? "Location & hours" : "Location & contact";
    const testid = hasHours ? "mv2-location-hours" : "mv2-location-contact";
    return (
      <div className="mv2-card mv2-location-hours" data-testid={testid} data-module-mode={hasHours ? "location_hours" : "contact_only"} data-hours-present={hasHours ? "true" : "false"}>
        <h2>{heading}</h2>
        {hasAddress && <p>{salon.address}</p>}
        {hasHours && <p style={{ color: "#6b7280" }}>{salon.hours_summary}</p>}
        {hasPhone && <p><a href={`tel:${salon.phone_e164}`}>{salon.phone_display}</a></p>}
      </div>
    );
  })();

  const businessIntro = (
    <div className="mv2-card" data-testid="mv2-business-intro">
      <h2>About {salon.name}</h2>
      <p>{offer.problem_addressed}</p>
      {offer.guarantee && <p><strong>Our promise:</strong> {offer.guarantee}</p>}
    </div>
  );

  const serviceExplanation = projection.canonical_service_public && (
    <div className="mv2-card" data-testid="mv2-service-explanation">
      <h2>{projection.canonical_service_public.name}</h2>
      <p>{projection.canonical_service_public.description_public}</p>
    </div>
  );

  const offerBlock = (
    <div className="mv2-card" data-testid="mv2-offer">
      <h2>The offer</h2>
      <p>{offer.customer_facing_offer}</p>
      {(ds?.content_bundle?.restrictions_public || offer.restrictions || []).length > 0 && (
        <ul className="mv2-restrictions" data-testid="mv2-restrictions">
          {(ds?.content_bundle?.restrictions_public || offer.restrictions || []).map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
    </div>
  );

  const proofBlock = projection.proof && projection.proof.length > 0 && (
    <div className="mv2-card" data-testid="mv2-proof">
      <h2>What guests say</h2>
      {projection.proof.map((p) => (
        <div className="mv2-proof-item" key={p.id}>
          <span className="mv2-rating">{"★".repeat(p.rating || 5)}</span>
          {p.text && <p>&quot;{p.text}&quot;</p>}
          <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>— {p.author_name}</p>
        </div>
      ))}
    </div>
  );

  const claimFormBlock = !claim ? (
    <div className="mv2-card" data-testid="mv2-claim-form" role="region" aria-labelledby="mv2-claim-heading">
      <h2 id="mv2-claim-heading">Claim this deal</h2>
      <p>Add your details and get your code right away. No spam.</p>
      <form onSubmit={submitClaim} className="mv2-form">
        <label htmlFor="mv2-name">Your name</label>
        <input id="mv2-name" data-testid="mv2-claim-name" type="text" autoComplete="name"
          value={formState.name} onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))} />
        <label htmlFor="mv2-phone">Mobile number</label>
        <input id="mv2-phone" data-testid="mv2-claim-phone" type="tel" inputMode="tel" autoComplete="tel"
          value={formState.phone} onChange={(e) => setFormState((s) => ({ ...s, phone: e.target.value }))} />
        <label htmlFor="mv2-email">Email (optional)</label>
        <input id="mv2-email" data-testid="mv2-claim-email" type="email" inputMode="email" autoComplete="email"
          value={formState.email} onChange={(e) => setFormState((s) => ({ ...s, email: e.target.value }))} />
        <div className="mv2-consent">
          {(reg.consent_options || []).map((opt) => (
            <label key={opt.key} data-testid={`mv2-consent-${opt.key}`}>
              <input type="checkbox" checked={!!formState[opt.key]}
                onChange={(e) => setFormState((s) => ({ ...s, [opt.key]: e.target.checked }))} />
              {opt.label}
            </label>
          ))}
          <p style={{ fontSize: 12, color: "#6b7280", margin: "8px 0 0" }}>
            You&apos;ll get your code even if you leave these unchecked. Consent policy: {projection.consent_policy_version}.
          </p>
        </div>
        {formError && <div className="mv2-error" data-testid="mv2-form-error" role="alert" aria-live="assertive">{formError}</div>}
        <button type="submit" className="mv2-primary-btn" data-testid="mv2-claim-submit" disabled={submitting}>
          {submitting ? "Working…" : "Claim my deal"}
        </button>
      </form>
    </div>
  ) : (
    <div className="mv2-card" data-testid="mv2-deal-code-card" role="region" aria-labelledby="mv2-code-heading">
      <div className="mv2-code-display" data-testid="mv2-code-display" aria-live="polite">
        <div id="mv2-code-heading">Your deal code</div>
        <div className="mv2-code" data-testid="mv2-deal-code" aria-label={`Deal code ${claim.code}`}>{claim.code}</div>
        <div style={{ fontSize: 13, color: "#6b7280" }}>Show this at your appointment. Expires {new Date(claim.expires_at).toLocaleDateString()}.</div>
      </div>
    </div>
  );

  const conversionBlock = claim && (
    <div className="mv2-card" data-testid="mv2-conversion" role="region" aria-labelledby="mv2-conversion-headline">
      <h2 id="mv2-conversion-headline" data-testid="mv2-conversion-headline">
        {resolution.language_style === "single_direct" && resolution.primary_method === "call" && "Call to book"}
        {resolution.language_style === "single_direct" && resolution.primary_method === "online" && "Book online"}
        {resolution.language_style === "single_direct" && resolution.primary_method === "text" && "Text us to book"}
        {resolution.language_style === "single_direct" && resolution.primary_method === "appointment_request" && "Request an appointment"}
        {resolution.language_style === "multi_choice" && "Ready to schedule?"}
        {resolution.language_style === "unavailable" && "Scheduling is unavailable"}
      </h2>
      {resolution.customer_safe_reason && <p style={{ color: "#6b7280", fontSize: 13 }}>{resolution.customer_safe_reason}</p>}
      {(resolution.effective_methods || []).includes("online") && (
        <button type="button" className="mv2-primary-btn" data-testid="mv2-method-online" onClick={() => goToBookingViaHandoff("online")}>
          Book online
        </button>
      )}
      {(resolution.effective_methods || []).includes("call") && (
        <a className={ (resolution.effective_methods || []).includes("online") ? "mv2-secondary-btn" : "mv2-primary-btn" } data-testid="mv2-method-call" href={`tel:${salon.phone_e164}`} onClick={() => onCallCtaTap("mv2_method_call")}>
          Call {salon.phone_display}
        </a>
      )}
      {(resolution.effective_methods || []).includes("text") && (
        <a className="mv2-secondary-btn" data-testid="mv2-method-text" href={`sms:${salon.phone_e164}`} onClick={(e) => onTextCtaTap(e, "mv2_method_text")}>
          Text {salon.phone_display}
        </a>
      )}
      {(resolution.effective_methods || []).includes("appointment_request") && (
        <button type="button" className="mv2-secondary-btn" data-testid="mv2-method-request" onClick={() => goToBookingViaHandoff("appointment_request")}>
          Request an appointment
        </button>
      )}
    </div>
  );

  // ── Checkpoint 7B — Business Introduction strategic-role modules ──
  const brandIdentityBlock = ds && (
    <div className="mv2-card mv2-brand-identity" data-testid="mv2-brand-identity">
      <h1 style={{ marginTop: 0 }}>{ds?.content_bundle?.headline || salon.name}</h1>
      {ds?.content_bundle?.supporting_copy && <p>{ds.content_bundle.supporting_copy}</p>}
      {ds?.content_bundle?.service_description && (
        <p style={{ color: "#6b7280", fontSize: 14 }}>{ds.content_bundle.service_description}</p>
      )}
    </div>
  );

  const trustSummaryBlock = ds && (
    <div className="mv2-card mv2-trust-summary" data-testid="mv2-trust-summary">
      <h2>Why guests trust us</h2>
      <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
        <li>Established, welcoming salon serving Flower Mound.</li>
        <li>Careful technicians and consistent service.</li>
        <li>Clean, calm, and easy to book.</li>
      </ul>
    </div>
  );

  const heroImageryBlock = ds && (
    <div className="mv2-card mv2-hero-imagery" data-testid="mv2-hero-imagery">
      <div style={{
        background: "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
        color: "#fff", padding: "48px 20px",
        borderRadius: 12, textAlign: "center",
      }}>
        <div style={{ fontSize: 14, opacity: 0.85 }}>Inside the salon</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 8 }}>{salon.name}</div>
      </div>
    </div>
  );

  const selfSelectionPathsBlock = ds && (
    <div className="mv2-card mv2-explore-services" data-testid="mv2-explore-services" role="region" aria-labelledby="mv2-explore-heading">
      <h2 id="mv2-explore-heading">Explore services</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <a href="/services/manicure" className="mv2-secondary-btn" data-testid="mv2-explore-manicure">Manicure services</a>
        <a href="/services/pedicure" className="mv2-secondary-btn" data-testid="mv2-explore-pedicure">Pedicure services</a>
        <a href="/services" className="mv2-secondary-btn" data-testid="mv2-explore-all">View all services</a>
      </div>
    </div>
  );

  const conversionActionBlock = ds && (
    <div className="mv2-card mv2-conversion-action" data-testid="mv2-conversion-action" role="region" aria-labelledby="mv2-conv-headline">
      <h2 id="mv2-conv-headline">Ready to book?</h2>
      <a className="mv2-primary-btn" data-testid="mv2-convert-book-online" href="/book" onClick={() => onMethodClick("online")}>
        Book online
      </a>
      <a className="mv2-secondary-btn" data-testid="mv2-convert-call" href={`tel:${salon.phone_e164}`} onClick={() => onCallCtaTap("mv2_convert_call")}>
        Call {salon.phone_display}
      </a>
    </div>
  );

  // Compose per platform blueprint (mobile vs desktop compositions differ
  // strategically — mobile stacks single-column thumb-first; desktop uses
  // a two-column offer-left/form-right layout with a persistent summary).
  const modules = {
    hero: heroBlock,
    business_intro: businessIntro,
    service_explanation: serviceExplanation,
    service_outcome: serviceOutcomeBlock,
    aggregate_rating: aggregateBlock,
    featured_testimonial: featuredTestimonialBlock,
    hero_image_proof: heroImageProofBlock,
    supporting_review_set: supportingReviewSetBlock,
    location_hours: locationHoursBlock,
    restrictions: null,
    offer: offerBlock,
    proof: proofBlock,
    claim: claimFormBlock,
    claim_form: claimFormBlock,
    conversion_action: isBusinessIntroduction ? conversionActionBlock : conversionBlock,
    // Business Introduction modules (Checkpoint 7B)
    brand_identity: brandIdentityBlock,
    trust_summary: trustSummaryBlock,
    hero_imagery: heroImageryBlock,
    self_selection_paths: selfSelectionPathsBlock,
  };

  const rendered = (
    <div className="mv2-page" data-testid="mv2-public-page"
      data-campaign-id={projection.campaign_id}
      data-campaign-slug={projection.campaign_slug}
      data-template-id={projection.template_id}
      data-template-version={projection.template_version}
      data-mobile-blueprint-version={projection.mobile_blueprint_version}
      data-desktop-blueprint-version={projection.desktop_blueprint_version}
      data-device={device}
      data-relationship-stage={projection.relationship_stage}
      data-service-context={projection.service_context}
      data-registration-treatment={reg?.type}
      data-conversion-language-style={resolution.language_style}
      data-lifecycle={projection.lifecycle}
      data-archetype-id={ds?.archetype?.id || null}
      data-archetype-version={ds?.archetype?.version || null}
      data-layout-blueprint-id={ds?.layout_blueprint?.id || null}
      data-layout-blueprint-version={ds?.layout_blueprint?.version || null}
      data-mobile-execution-id={ds?.mobile_execution?.id || null}
      data-desktop-execution-id={ds?.desktop_execution?.id || null}
      data-content-bundle-id={ds?.content_bundle?.id || null}
      data-content-bundle-version={ds?.content_bundle?.version || null}
      data-proof-set-id={ds?.proof_set?.id || null}
      data-proof-set-version={ds?.proof_set?.version || null}
      data-aggregate-snapshot-id={ds?.aggregate?.id || null}
      data-route-purpose={projection.route_purpose || null}
      data-indexing-policy={projection.indexing_policy || null}
      data-campaign-temporary={projection.campaign_temporary === true ? "true" : (projection.campaign_temporary === false ? "false" : null)}
    >
      <div className="mv2-wrap">
        {isBusinessIntroduction && desktopMode && isBiConversionFirst ? (
          // Layout C — Conversion-first Compact desktop.
          <div className="mv2-desktop-grid mv2-layout-stack" data-testid="mv2-desktop-primary" data-bi-layout="conversion_first_compact">
            <div>
              {modules.conversion_action}
              {modules.brand_identity}
              {modules.aggregate_rating}
              {modules.featured_testimonial}
              {modules.location_hours}
            </div>
            <div className="mv2-desktop-summary" data-testid="mv2-desktop-summary">
              {modules.self_selection_paths}
            </div>
          </div>
        ) : isBusinessIntroduction && desktopMode && isBiVisualHero ? (
          // Layout D — Brand-and-Visual Hero desktop (full-width hero above the split).
          <>
            <div data-testid="mv2-desktop-hero" data-bi-layout="brand_and_visual_hero">
              {modules.hero_imagery}
              {modules.brand_identity}
            </div>
            <div className="mv2-desktop-grid mv2-layout-stack" data-testid="mv2-desktop-primary" data-bi-layout="brand_and_visual_hero">
              <div>
                {modules.trust_summary}
                {modules.aggregate_rating}
                {modules.featured_testimonial}
                {modules.location_hours}
              </div>
              <div className="mv2-desktop-summary" data-testid="mv2-desktop-summary">
                {modules.self_selection_paths}
                {modules.conversion_action}
              </div>
            </div>
          </>
        ) : isBusinessIntroduction && desktopMode && isBiServiceExplorer ? (
          // Layout E — Service-Explorer First desktop.
          <div className="mv2-desktop-grid mv2-layout-stack" data-testid="mv2-desktop-primary" data-bi-layout="service_explorer_first">
            <div>
              {modules.brand_identity}
              {modules.self_selection_paths}
              {modules.location_hours}
            </div>
            <div className="mv2-desktop-summary" data-testid="mv2-desktop-summary">
              {modules.aggregate_rating}
              {modules.featured_testimonial}
              {modules.conversion_action}
            </div>
          </div>
        ) : isBusinessIntroduction && desktopMode && isBiReputationSplit ? (
          // Business Introduction Layout B — desktop = 2-column
          // (reputation & imagery left · self-selection & conversion right).
          <div className="mv2-desktop-grid mv2-layout-split" data-testid="mv2-desktop-primary" data-bi-layout="reputation_and_experience_split">
            <div data-testid="mv2-desktop-left-column" style={{ minWidth: 0 }}>
              {modules.aggregate_rating}
              {modules.hero_imagery}
              {modules.brand_identity}
              {modules.featured_testimonial}
              {modules.location_hours}
            </div>
            <div className="mv2-desktop-summary" data-testid="mv2-desktop-summary">
              {modules.self_selection_paths}
              {modules.conversion_action}
            </div>
          </div>
        ) : isBusinessIntroduction && desktopMode ? (
          // Business Introduction Layout A — desktop content flow + right summary.
          <div className="mv2-desktop-grid mv2-layout-stack" data-testid="mv2-desktop-primary" data-bi-layout="brand_and_trust_narrative">
            <div>
              {modules.brand_identity}
              {modules.trust_summary}
              {modules.aggregate_rating}
              {modules.featured_testimonial}
              {modules.location_hours}
            </div>
            <div className="mv2-desktop-summary" data-testid="mv2-desktop-summary">
              {modules.self_selection_paths}
              {modules.conversion_action}
            </div>
          </div>
        ) : isBusinessIntroduction ? (
          // Business Introduction — mobile single-column, layout-specific module order.
          (composition || []).map((m) => <React.Fragment key={m}>{modules[m]}</React.Fragment>)
        ) : desktopMode && isSplitLayout ? (
          // Layout B: Proof-and-Claim Split — desktop = 2-column (proof left, claim right).
          <div className="mv2-desktop-grid mv2-layout-split" data-testid="mv2-desktop-primary">
            <div data-testid="mv2-desktop-left-column" style={{ minWidth: 0 }}>
              {modules.service_outcome}
              {modules.hero_image_proof}
              {modules.supporting_review_set}
              {modules.location_hours}
            </div>
            <div className="mv2-desktop-summary" data-testid="mv2-desktop-summary">
              {modules.offer}
              {claimFormBlock}
              {conversionBlock}
            </div>
          </div>
        ) : desktopMode ? (
          // Layout A: Proof Stack — desktop uses left content flow + right summary column.
          <div className="mv2-desktop-grid mv2-layout-stack" data-testid="mv2-desktop-primary">
            <div>
              {(composition || []).filter((m) => m !== "claim" && m !== "claim_form" && m !== "conversion_action" && m !== "offer").map((m) => <React.Fragment key={m}>{modules[m]}</React.Fragment>)}
              {modules.offer}
            </div>
            <div className="mv2-desktop-summary" data-testid="mv2-desktop-summary">
              {claimFormBlock}
              {conversionBlock}
            </div>
          </div>
        ) : (
          // Mobile — single-column, layout-specific module order.
          (composition || []).map((m) => <React.Fragment key={m}>{modules[m]}</React.Fragment>)
        )}
        {/* Shared public Privacy · Privacy Choices strip (§11 A/B).
            Rendered on the actual customer projection only; the internal
            Owner preview frame wraps this but is explicitly excluded from
            being treated as a customer surface (§13). */}
        <div
          className="mv2-privacy-strip"
          data-testid="mv2-privacy-strip"
          style={{ padding: "24px 20px 32px", display: "flex", justifyContent: "center" }}
        >
          <PublicPrivacyLinks />
        </div>
      </div>
    </div>
  );

  return isPreview ? previewFrame(rendered, projection) : rendered;
}

function previewFrame(inner, projection) {
  return (
    <div className="mv2-preview-frame" data-testid="mv2-preview-frame">
      <div className="mv2-preview-label" data-testid="mv2-preview-label">
        Internal preview · not visible to customers
      </div>
      <div style={{ fontSize: 12, color: "#7c2d12", marginBottom: 12 }}>
        Template <strong>{projection.template_id}</strong> v{projection.template_version} ·
        Mobile <strong>{projection.mobile_blueprint_version}</strong> ·
        Desktop <strong>{projection.desktop_blueprint_version}</strong> ·
        Stage <strong>{projection.relationship_stage}</strong> ·
        Distribution <strong>{projection.distribution}</strong>
      </div>
      {/* Checkpoint 7B — Owner Preview information panel */}
      <div
        data-testid="mv2-owner-preview-info"
        style={{
          fontSize: 12,
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderRadius: 8,
          padding: "10px 14px",
          marginBottom: 12,
          color: "#78350f",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Route · SEO · Chip · Continuity</div>
        <div data-testid="mv2-owner-preview-route">
          Route purpose: <strong>{projection.route_purpose || "not set"}</strong>
          {" · "}Indexing: <strong>{projection.indexing_policy || "default"}</strong>
          {projection.campaign_temporary === true && (
            <span> · <strong>Temporary campaign</strong></span>
          )}
        </div>
        <div data-testid="mv2-owner-preview-chip">
          Staff Portal chip: <strong>absent (focused route)</strong>
        </div>
        {projection.ad_promise_snapshot && (
          <div data-testid="mv2-owner-preview-promise" style={{ marginTop: 6 }}>
            Ad promise snapshot:
            {" "}
            <strong>{projection.ad_promise_snapshot.headline || "—"}</strong>
            {projection.ad_promise_snapshot.offer && (
              <span> · {projection.ad_promise_snapshot.offer}</span>
            )}
          </div>
        )}
      </div>
      {inner}
    </div>
  );
}
